import { useEffect, useState } from "react";
import { Loader2, Save, ArrowUp, ArrowDown, Eye, EyeOff, Megaphone, Layout, Image as ImageIcon, PanelBottom, Star, Search, ShoppingBag, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { OVERLINE, H1, btnPrimary, Panel, useAdmin } from "@/components/admin/ui";

const inp = "w-full border border-[#E5E2DC] bg-white px-3 py-2 mt-1 text-sm outline-none focus:border-[#1F2A22]";
const dtLocal = (v) => (v ? String(v).slice(0, 16) : "");

function promoStatus(cfg) {
  if (!cfg?.enabled) return { text: "Off", cls: "bg-[#E5E2DC] text-[#5C6B61]" };
  const now = Date.now();
  const start = cfg.start_at ? new Date(cfg.start_at).getTime() : null;
  const end = cfg.end_at ? new Date(cfg.end_at).getTime() : null;
  if (start && now < start) return { text: "Scheduled", cls: "bg-[#8A5A44]/15 text-[#8A5A44]" };
  if (end && now > end) return { text: "Expired", cls: "bg-[#B04A3A]/15 text-[#B04A3A]" };
  return { text: "Live", cls: "bg-[#2E7D46]/15 text-[#2E7D46]" };
}
const ScheduleBadge = ({ cfg }) => {
  const s = promoStatus(cfg);
  return <span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 ${s.cls}`} data-testid="promo-status">{s.text}</span>;
};
const Field = ({ label, hint, children }) => (
  <label className="block">
    <span className="text-[11px] font-serif uppercase tracking-[0.12em] text-[#5C6B61]">{label}</span>
    {children}
    {hint && <span className="block text-[11px] text-[#8A8478] mt-1">{hint}</span>}
  </label>
);

function FeaturedPicker({ label, options, selected, onToggle, labelFn, subFn, testid }) {
  const [q, setQ] = useState("");
  const ql = q.trim().toLowerCase();
  const rows = ql ? options.filter((o) => (labelFn(o) || "").toLowerCase().includes(ql)) : options;
  return (
    <div data-testid={`featured-picker-${testid}`}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-serif uppercase tracking-[0.12em] text-[#5C6B61]">{label}</span>
        <span className="text-[11px] text-[#8A5A44]">{selected.length} selected</span>
      </div>
      <div className="relative mt-1">
        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#5C6B61]" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${label.toLowerCase()}…`} className="w-full border border-[#E5E2DC] bg-white pl-8 pr-3 py-1.5 text-sm outline-none focus:border-[#1F2A22]" data-testid={`featured-search-${testid}`} />
      </div>
      <div className="border border-[#E5E2DC] mt-2 max-h-52 overflow-y-auto divide-y divide-[#E5E2DC]">
        {rows.length === 0 ? <p className="p-3 text-xs text-[#8A8478]">No {label.toLowerCase()} available yet.</p> :
          rows.map((o) => {
            const on = selected.includes(o.id);
            return (
              <button key={o.id} type="button" onClick={() => onToggle(o.id)} className={`w-full flex items-center gap-2 px-3 py-2 text-left ${on ? "bg-[#FF6A00]/5" : "bg-white hover:bg-[#F2F0EB]"}`} data-testid={`featured-opt-${testid}-${o.id}`}>
                <Star size={13} className={on ? "text-[#FF6A00] fill-[#FF6A00]" : "text-[#C7C2B8]"} />
                <span className="flex-1 min-w-0">
                  <span className="block text-sm text-[#1F2A22] truncate">{labelFn(o)}</span>
                  {subFn(o) && <span className="block text-[11px] text-[#5C6B61] truncate">{subFn(o)}</span>}
                </span>
              </button>
            );
          })}
      </div>
    </div>
  );
}

export default function AdminWebsite() {
  const { admin } = useAdmin();
  const canEdit = admin?.permissions?.includes("website");
  const [data, setData] = useState(null);
  const [options, setOptions] = useState({ hunts: [], businesses: [], products: [] });
  const [goodsSel, setGoodsSel] = useState([]);      // ordered selected homepage Goods products
  const [goodsAvail, setGoodsAvail] = useState([]);  // all published products
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.adminSiteContent().then(setData).catch(() => setData(null)); }, []);
  useEffect(() => { api.adminWebsiteOptions().then(setOptions).catch(() => {}); }, []);
  useEffect(() => { api.admHomepageGoods().then((d) => { setGoodsSel(d.selected || []); setGoodsAvail(d.available || []); }).catch(() => {}); }, []);

  const setAnn = (k) => (e) => setData({ ...data, announcement: { ...data.announcement, [k]: e.target.value } });
  const setHero = (k) => (e) => setData({ ...data, hero: { ...data.hero, [k]: e.target.value } });
  const setFooter = (k) => (e) => setData({ ...data, footer: { ...data.footer, [k]: e.target.value } });
  const setGoods = (k, v) => setData({ ...data, goods: { ...(data.goods || {}), [k]: v } });
  const setFeat = (k, v) => setData({ ...data, featured: { ...data.featured, [k]: v } });
  const toggleFeat = (kind) => (id) => {
    const cur = data.featured?.[kind] || [];
    setFeat(kind, cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);
  };

  // Homepage Goods product selection (max 4 shown on the homepage; up to 8 stored)
  const addGood = (id) => {
    const p = goodsAvail.find((x) => x.id === id);
    if (!p || goodsSel.some((x) => x.id === id)) return;
    setGoodsSel([...goodsSel, p]);
  };
  const removeGood = (id) => setGoodsSel(goodsSel.filter((x) => x.id !== id));
  const moveGood = (i, dir) => {
    const arr = [...goodsSel];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setGoodsSel(arr);
  };

  const toggleSection = (i) => {
    const sections = data.sections.map((s, idx) => (idx === i ? { ...s, enabled: !s.enabled } : s));
    setData({ ...data, sections });
  };
  const move = (i, dir) => {
    const sections = [...data.sections];
    const j = i + dir;
    if (j < 0 || j >= sections.length) return;
    [sections[i], sections[j]] = [sections[j], sections[i]];
    setData({ ...data, sections: sections.map((s, idx) => ({ ...s, order: idx })) });
  };

  const save = async () => {
    setBusy(true);
    try {
      const g = data.goods || {};
      const updated = await api.adminSiteContentUpdate({
        announcement: data.announcement,
        hero: data.hero,
        footer: data.footer,
        goods: {
          enabled: g.enabled !== false,
          eyebrow: g.eyebrow || "",
          headline: g.headline || "",
          description: g.description || "",
          button_text: g.button_text || "",
        },
        featured: {
          enabled: !!data.featured?.enabled,
          title: data.featured?.title || "",
          hunts: data.featured?.hunts || [],
          businesses: data.featured?.businesses || [],
          products: data.featured?.products || [],
          start_at: data.featured?.start_at || null,
          end_at: data.featured?.end_at || null,
        },
        sections: data.sections.map((s) => ({ key: s.key, enabled: s.enabled })),
      });
      await api.admHomepageGoodsUpdate(goodsSel.map((p) => p.id));
      setData(updated);
      toast.success("Homepage updated — it's live now");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally { setBusy(false); }
  };

  if (!data) return <div className="py-20 grid place-items-center"><Loader2 className="animate-spin text-[#1F2A22]" /></div>;

  return (
    <div className="p-6 md:p-8 max-w-4xl" data-testid="admin-website">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div><span className={OVERLINE}>Website</span><h1 className={`${H1} mt-1`}>Homepage Manager</h1></div>
        {canEdit && <button onClick={save} disabled={busy} className={btnPrimary} data-testid="website-save">{busy ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save & publish</button>}
      </div>
      {!canEdit && <p className="mt-4 text-sm text-[#8A5A44]">You have read-only access to this page.</p>}

      <div className="mt-6 space-y-6">
        {/* Announcement */}
        <Panel title={<span className="flex items-center gap-2"><Megaphone size={16} /> Announcement bar <ScheduleBadge cfg={data.announcement} /></span>}>
          <label className="flex items-center gap-2 text-sm text-[#1F2A22] mb-4">
            <input type="checkbox" checked={!!data.announcement.enabled} onChange={(e) => setData({ ...data, announcement: { ...data.announcement, enabled: e.target.checked } })} disabled={!canEdit} data-testid="ann-enabled" />
            Show announcement bar at the top of the homepage
          </label>
          <div className="space-y-3">
            <Field label="Text"><input value={data.announcement.text || ""} onChange={setAnn("text")} disabled={!canEdit} className={inp} placeholder="e.g. Fall archery season is now open" data-testid="ann-text" /></Field>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Link (optional)"><input value={data.announcement.link || ""} onChange={setAnn("link")} disabled={!canEdit} className={inp} placeholder="/advertise" data-testid="ann-link" /></Field>
              <Field label="Link label"><input value={data.announcement.link_label || ""} onChange={setAnn("link_label")} disabled={!canEdit} className={inp} placeholder="Learn more" data-testid="ann-link-label" /></Field>
            </div>
            <div className="grid sm:grid-cols-2 gap-3 pt-1">
              <Field label="Show from" hint="Optional — leave blank to show immediately."><input type="datetime-local" value={dtLocal(data.announcement.start_at)} onChange={(e) => setAnn("start_at")({ target: { value: e.target.value || null } })} disabled={!canEdit} className={inp} data-testid="ann-start" /></Field>
              <Field label="Hide after" hint="Optional — leave blank to run indefinitely."><input type="datetime-local" value={dtLocal(data.announcement.end_at)} onChange={(e) => setAnn("end_at")({ target: { value: e.target.value || null } })} disabled={!canEdit} className={inp} data-testid="ann-end" /></Field>
            </div>
            <div>
              <span className="text-[11px] font-serif uppercase tracking-[0.12em] text-[#5C6B61]">Live preview</span>
              {data.announcement.text ? (
                <div className="bg-[#FF6A00] text-white text-center text-sm py-2.5 px-4 mt-1" data-testid="ann-preview">
                  <span className="font-medium">{data.announcement.text}</span>
                  {data.announcement.link && <span className="underline underline-offset-2 ml-2 font-semibold">{data.announcement.link_label || "Learn more"}</span>}
                </div>
              ) : (
                <p className="text-[11px] text-[#8A8478] mt-1">Add text above to preview the bar.</p>
              )}
            </div>
          </div>
        </Panel>

        {/* Hero */}
        <Panel title={<span className="flex items-center gap-2"><ImageIcon size={16} /> Hero</span>}>
          <div className="space-y-3">
            <Field label="Overline"><input value={data.hero.overline || ""} onChange={setHero("overline")} disabled={!canEdit} className={inp} data-testid="hero-overline-input" /></Field>
            <Field label="Headline" hint="Use a line break for two-line headlines."><textarea rows={2} value={data.hero.headline || ""} onChange={setHero("headline")} disabled={!canEdit} className={inp} data-testid="hero-headline-input" /></Field>
            <Field label="Subheadline"><textarea rows={3} value={data.hero.subheadline || ""} onChange={setHero("subheadline")} disabled={!canEdit} className={inp} data-testid="hero-subheadline-input" /></Field>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Primary button label"><input value={data.hero.cta_label || ""} onChange={setHero("cta_label")} disabled={!canEdit} className={inp} data-testid="hero-cta-input" /></Field>
              <Field label="Background image URL"><input value={data.hero.background_image || ""} onChange={setHero("background_image")} disabled={!canEdit} className={inp} data-testid="hero-bg-input" /></Field>
            </div>
            {data.hero.background_image && (
              <div className="h-40 bg-[#1F2A22] overflow-hidden relative mt-1" data-testid="hero-bg-preview">
                <img src={data.hero.background_image} alt="Hero preview" className="w-full h-full object-cover opacity-50" />
                <div className="absolute inset-0 flex items-center px-6"><span className="text-white font-serif text-3xl whitespace-pre-line">{data.hero.headline}</span></div>
              </div>
            )}
          </div>
        </Panel>

        {/* Sections */}
        <Panel title={<span className="flex items-center gap-2"><Layout size={16} /> Homepage sections</span>}>
          <p className="text-xs text-[#5C6B61] mb-3">Reorder sections and toggle visibility. Hero and footer always show.</p>
          <div className="border border-[#E5E2DC] divide-y divide-[#E5E2DC]" data-testid="section-list">
            {data.sections.map((s, i) => (
              <div key={s.key} className={`flex items-center gap-3 px-4 py-2.5 ${s.enabled ? "bg-white" : "bg-[#F2F0EB]"}`} data-testid={`section-row-${s.key}`}>
                <span className="text-[11px] tabular-nums text-[#8A8478] w-5">{i + 1}</span>
                <span className={`flex-1 text-sm ${s.enabled ? "text-[#1F2A22]" : "text-[#8A8478] line-through"}`}>{s.label}</span>
                {canEdit && <>
                  <button onClick={() => move(i, -1)} disabled={i === 0} className="p-1 text-[#5C6B61] hover:text-[#1F2A22] disabled:opacity-30" aria-label="Move up" data-testid={`section-up-${s.key}`}><ArrowUp size={15} /></button>
                  <button onClick={() => move(i, 1)} disabled={i === data.sections.length - 1} className="p-1 text-[#5C6B61] hover:text-[#1F2A22] disabled:opacity-30" aria-label="Move down" data-testid={`section-down-${s.key}`}><ArrowDown size={15} /></button>
                  <button onClick={() => toggleSection(i)} className={`p-1 ${s.enabled ? "text-[#1F2A22]" : "text-[#8A8478]"} hover:text-[#FF6A00]`} aria-label="Toggle visibility" data-testid={`section-toggle-${s.key}`}>{s.enabled ? <Eye size={15} /> : <EyeOff size={15} />}</button>
                </>}
              </div>
            ))}
          </div>
        </Panel>

        {/* HomeRange Goods (homepage editorial section) */}
        <Panel title={<span className="flex items-center gap-2"><ShoppingBag size={16} /> HomeRange Goods section</span>}>
          <label className="flex items-center gap-2 text-sm text-[#1F2A22] mb-4">
            <input type="checkbox" checked={data.goods?.enabled !== false} onChange={(e) => setGoods("enabled", e.target.checked)} disabled={!canEdit} data-testid="goods-enabled" />
            Show the HomeRange Goods section on the homepage
          </label>
          <div className="space-y-3">
            <Field label="Eyebrow"><input value={data.goods?.eyebrow || ""} onChange={(e) => setGoods("eyebrow", e.target.value)} disabled={!canEdit} className={inp} placeholder="HOMERANGE GOODS" data-testid="goods-eyebrow-input" /></Field>
            <Field label="Headline"><input value={data.goods?.headline || ""} onChange={(e) => setGoods("headline", e.target.value)} disabled={!canEdit} className={inp} placeholder="Made for wherever you call home." data-testid="goods-headline-input" /></Field>
            <Field label="Supporting copy"><textarea rows={2} value={data.goods?.description || ""} onChange={(e) => setGoods("description", e.target.value)} disabled={!canEdit} className={inp} data-testid="goods-description-input" /></Field>
            <Field label="Button text" hint="The button always links to the /shop storefront."><input value={data.goods?.button_text || ""} onChange={(e) => setGoods("button_text", e.target.value)} disabled={!canEdit} className={inp} placeholder="SHOP HOMERANGE" data-testid="goods-button-input" /></Field>
          </div>

          <div className="mt-5">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-serif uppercase tracking-[0.12em] text-[#5C6B61]">Featured products</span>
              <span className={`text-[11px] ${goodsSel.length > 4 ? "text-[#B04A3A]" : "text-[#8A5A44]"}`}>{goodsSel.length} chosen · first 4 show</span>
            </div>
            <p className="text-[11px] text-[#8A8478] mt-1 mb-2">Only published products appear here. The homepage shows the first 4 in this order. To change a tile image, set the product&apos;s “homepage display image” in the product editor.</p>

            {/* Selected (ordered) */}
            <div className="border border-[#E5E2DC] divide-y divide-[#E5E2DC] bg-white" data-testid="goods-selected-list">
              {goodsSel.length === 0 ? (
                <p className="p-3 text-xs text-[#8A8478]">No products chosen yet — add published products below.</p>
              ) : goodsSel.map((p, i) => (
                <div key={p.id} className={`flex items-center gap-3 px-3 py-2 ${i < 4 ? "bg-white" : "bg-[#F2F0EB]"}`} data-testid={`goods-sel-${p.id}`}>
                  <span className="text-[11px] tabular-nums text-[#8A8478] w-4">{i + 1}</span>
                  <div className="w-10 h-10 bg-bone overflow-hidden shrink-0">{p.image && <img src={p.image} alt="" className="w-full h-full object-cover" />}</div>
                  <span className="flex-1 min-w-0 text-sm text-[#1F2A22] truncate">{p.title}{i >= 4 && <span className="text-[10px] text-[#8A8478] ml-2">(hidden)</span>}</span>
                  {canEdit && <>
                    <button onClick={() => moveGood(i, -1)} disabled={i === 0} className="p-1 text-[#5C6B61] hover:text-[#1F2A22] disabled:opacity-30" aria-label="Move up" data-testid={`goods-up-${p.id}`}><ArrowUp size={15} /></button>
                    <button onClick={() => moveGood(i, 1)} disabled={i === goodsSel.length - 1} className="p-1 text-[#5C6B61] hover:text-[#1F2A22] disabled:opacity-30" aria-label="Move down" data-testid={`goods-down-${p.id}`}><ArrowDown size={15} /></button>
                    <button onClick={() => removeGood(p.id)} className="p-1 text-[#B04A3A] hover:text-[#8A3A2C]" aria-label="Remove" data-testid={`goods-remove-${p.id}`}><Trash2 size={15} /></button>
                  </>}
                </div>
              ))}
            </div>

            {/* Add from published */}
            {canEdit && (() => {
              const remaining = goodsAvail.filter((p) => !goodsSel.some((s) => s.id === p.id));
              return (
                <div className="mt-3">
                  <span className="text-[11px] font-serif uppercase tracking-[0.12em] text-[#5C6B61]">Add a published product</span>
                  <div className="border border-[#E5E2DC] mt-1 max-h-44 overflow-y-auto divide-y divide-[#E5E2DC] bg-white" data-testid="goods-available-list">
                    {remaining.length === 0 ? <p className="p-3 text-xs text-[#8A8478]">All published products are already added.</p> :
                      remaining.map((p) => (
                        <button key={p.id} type="button" onClick={() => addGood(p.id)} className="w-full flex items-center gap-2 px-3 py-2 text-left bg-white hover:bg-[#F2F0EB]" data-testid={`goods-add-${p.id}`}>
                          <Plus size={13} className="text-[#FF6A00]" />
                          <div className="w-8 h-8 bg-bone overflow-hidden shrink-0">{p.image && <img src={p.image} alt="" className="w-full h-full object-cover" />}</div>
                          <span className="flex-1 min-w-0 text-sm text-[#1F2A22] truncate">{p.title}</span>
                        </button>
                      ))}
                  </div>
                </div>
              );
            })()}
          </div>
        </Panel>

        {/* Featured picks */}
        <Panel title={<span className="flex items-center gap-2"><Star size={16} /> Featured picks <ScheduleBadge cfg={data.featured} /></span>}>
          <label className="flex items-center gap-2 text-sm text-[#1F2A22] mb-4">
            <input type="checkbox" checked={!!data.featured?.enabled} onChange={(e) => setFeat("enabled", e.target.checked)} disabled={!canEdit} data-testid="featured-enabled" />
            Show a Featured strip below the hero on the homepage
          </label>
          <Field label="Strip title"><input value={data.featured?.title || ""} onChange={(e) => setFeat("title", e.target.value)} disabled={!canEdit} className={inp} placeholder="Featured on HomeRange" data-testid="featured-title-input" /></Field>
          <div className="grid sm:grid-cols-2 gap-3 mt-3">
            <Field label="Show from" hint="Optional — leave blank to show immediately."><input type="datetime-local" value={dtLocal(data.featured?.start_at)} onChange={(e) => setFeat("start_at", e.target.value || null)} disabled={!canEdit} className={inp} data-testid="featured-start" /></Field>
            <Field label="Hide after" hint="Optional — leave blank to run indefinitely."><input type="datetime-local" value={dtLocal(data.featured?.end_at)} onChange={(e) => setFeat("end_at", e.target.value || null)} disabled={!canEdit} className={inp} data-testid="featured-end" /></Field>
          </div>
          <p className="text-xs text-[#5C6B61] mt-3">Pick real hunts, brands and products to feature. Featured products are also badged and floated to the front inside the Gear section.</p>
          <div className="grid md:grid-cols-3 gap-4 mt-3">
            <FeaturedPicker label="Hunts" testid="hunts" options={options.hunts} selected={data.featured?.hunts || []} onToggle={toggleFeat("hunts")}
              labelFn={(o) => o.title} subFn={(o) => [o.species, o.jurisdiction].filter(Boolean).join(" · ")} />
            <FeaturedPicker label="Brands" testid="businesses" options={options.businesses} selected={data.featured?.businesses || []} onToggle={toggleFeat("businesses")}
              labelFn={(o) => o.name} subFn={(o) => [o.category, o.location].filter(Boolean).join(" · ")} />
            <FeaturedPicker label="Products" testid="products" options={options.products} selected={data.featured?.products || []} onToggle={toggleFeat("products")}
              labelFn={(o) => o.title} subFn={(o) => [o.business_name, o.price != null ? `$${o.price}` : null].filter(Boolean).join(" · ")} />
          </div>
        </Panel>

        {/* Footer */}
        <Panel title={<span className="flex items-center gap-2"><PanelBottom size={16} /> Footer</span>}>
          <div className="space-y-3">
            <Field label="Tagline"><input value={data.footer.tagline || ""} onChange={setFooter("tagline")} disabled={!canEdit} className={inp} data-testid="footer-tagline-input" /></Field>
            <Field label="Motto"><input value={data.footer.motto || ""} onChange={setFooter("motto")} disabled={!canEdit} className={inp} data-testid="footer-motto-input" /></Field>
          </div>
        </Panel>

        {canEdit && <div className="flex justify-end"><button onClick={save} disabled={busy} className={btnPrimary} data-testid="website-save-bottom">{busy ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save & publish</button></div>}
      </div>
    </div>
  );
}
